// «الصيانة» — formatting shared by the page and its sheets. Every value the server sends is a
// plain number / ISO string; nothing here computes a figure.
import { fmtRiyadhDateTime } from "@/lib/format";

export const fmtTemplate = (s, vars) =>
    String(s || "").replace(/\{(\w+)\}/g, (_m, k) => (vars && vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ""));

const nf = (lang, digits = 0) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", {
    minimumFractionDigits: digits, maximumFractionDigits: digits, numberingSystem: "latn",
});

export const fmtInt = (v, lang) => (v === null || v === undefined ? "—" : nf(lang).format(v));

export const fmtPct = (v, lang) => (v === null || v === undefined ? "—" : `${nf(lang, 1).format(v)}%`);

export const fmtSar = (v, lang, t) => (v === null || v === undefined ? "—" : `${nf(lang, 2).format(v)} ${t("mt_sar")}`);

/** Minutes as «3س 20د» / "3h 20m" (days above 24 h). */
export function fmtDuration(min, t) {
    if (min === null || min === undefined || Number.isNaN(Number(min))) return "—";
    const m = Math.max(0, Math.round(Number(min)));
    if (m < 60) return `${m}${t("mt_unit_min")}`;
    if (m < 1440) {
        const h = Math.floor(m / 60);
        const r = m % 60;
        return r ? `${h}${t("mt_unit_hour")} ${r}${t("mt_unit_min")}` : `${h}${t("mt_unit_hour")}`;
    }
    const d = Math.floor(m / 1440);
    const h = Math.floor((m % 1440) / 60);
    return h ? `${d}${t("mt_unit_day")} ${h}${t("mt_unit_hour")}` : `${d}${t("mt_unit_day")}`;
}

export const fmtWhen = (iso, lang) => (iso ? fmtRiyadhDateTime(iso, lang) : "—");

/** A known code's label, else the code itself (the maintenance system may add values). */
function labelOr(t, key, raw) {
    const v = t(key);
    return v === key ? raw || "—" : v;
}

export const statusLabel = (t, s) => labelOr(t, `mt_st_${s}`, s);
export const critLabel = (t, c) => labelOr(t, `mt_cr_${c || "unknown"}`, c);
export const errorLabel = (t, code) => labelOr(t, `mt_err_${code}`, code);

const CRIT_TONE = {
    emergency: "destructive", critical: "destructive", urgent: "destructive",
    high: "warning", medium: "default", normal: "default", low: "secondary",
};
export const critTone = (c) => CRIT_TONE[c] || "outline";

const STATUS_TONE = {
    new: "default", accepted: "default", assigned: "secondary", in_progress: "secondary", reopened: "warning",
    completed: "success", closed: "brown", rejected: "outline", cancelled: "outline",
};
export const statusTone = (s) => STATUS_TONE[s] || "outline";

/** A Riyadh calendar day as YYYY-MM-DD, `offsetDays` from today. */
export function riyadhDay(offsetDays = 0) {
    const d = new Date(Date.now() + 3 * 3600 * 1000 + offsetDays * 86400000);
    return d.toISOString().slice(0, 10);
}

export const PRESETS = {
    7: () => ({ from: riyadhDay(-6), to: riyadhDay(0) }),
    30: () => ({ from: riyadhDay(-29), to: riyadhDay(0) }),
    90: () => ({ from: riyadhDay(-89), to: riyadhDay(0) }),
    mtd: () => ({ from: `${riyadhDay(0).slice(0, 8)}01`, to: riyadhDay(0) }),
};

/** A part line's name / quantity / unit price, whatever the maintenance system calls them. */
export const partName = (p) => p.name || p.part_name || p.item_name || p.description || p.code || "—";
export const partQty = (p) => p.quantity ?? p.qty ?? null;
export const partUnit = (p) => p.unit_cost ?? p.unit_price ?? p.price ?? null;

/** A root cause's label in the chosen language, whatever its shape. */
export function rootCauseLabel(rc, lang) {
    if (!rc) return null;
    const ar = rc.name_ar || rc.label_ar || rc.title_ar;
    const en = rc.name_en || rc.label_en || rc.title_en;
    return (lang === "ar" ? ar || en : en || ar) || rc.name || rc.label || rc.code || rc.id || null;
}

export const bucketName = (row, lang) => (lang === "ar" ? row?.name_ar || row?.name_en : row?.name_en || row?.name_ar) || "—";
export const itemBranchName = (it, lang) => (lang === "ar" ? it.branch_name_ar || it.branch_name_en : it.branch_name_en || it.branch_name_ar) || it.location_code || "—";
export const itemLocationName = (it, lang) => (lang === "ar" ? it.location_name_ar || it.location_name_en : it.location_name_en || it.location_name_ar) || it.location_code || "—";
